import { ICloneable } from '../../contracts/cloneable.js';
import { IDataComparable } from '../../contracts/data-comparable.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { cloneListOfCloneable, cloneMap, DataModel, isSameData,
  isSameList, isSameMap, mapToData, SerializationContext, SerializedData } from '../dto/dto.js';
import { FestivalCategoryType } from './festival-category-type.js';

export class FestivalItem implements ICloneable, IDataComparable {
  constructor(
        public readonly index: number,
        public readonly id: string,
  ) { }

  static decode(map: SerializedData): FestivalItem {
    return new FestivalItem(
        MapUtils.get<number>(map, 'index'),
        MapUtils.get<string>(map, 'id'),
    );
  }

  encode(): SerializedData {
    return {
      'index': this.index,
      'id': this.id,
    };
  }

  clone(): this {
    return new FestivalItem(
        this.index,
        this.id,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.index === this.index &&
        other.id === this.id;
  }
}

export class FestivalCategory implements ICloneable, IDataComparable {
  constructor(
    public readonly index: number,
    public readonly categoryId: string,
    public readonly items: FestivalItem[],
    public readonly type: FestivalCategoryType,
  ) { }

  static decode(map: SerializedData): FestivalCategory {
    return new FestivalCategory(
        MapUtils.get<number>(map, 'index'),
        MapUtils.get<string>(map, 'categoryId'),
        MapUtils.getList<FestivalItem>(map, 'items', (data: unknown) => {
          return FestivalItem.decode(data as SerializedData);
        }),
        toEnumFromString(MapUtils.get<string>(map, 'type'), FestivalCategoryType),
    );
  }

  encode(): SerializedData {
    return {
      'index': this.index,
      'categoryId': this.categoryId,
      'items': this.items.map((e: FestivalItem) => e.encode()),
      'type': fromEnumAsString(this.type),
    };
  }

  clone(): this {
    return new FestivalCategory(
        this.index,
        this.categoryId,
        cloneListOfCloneable(this.items),
        this.type,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.index === this.index &&
        other.categoryId === this.categoryId &&
        isSameList(other.items, this.items, (a: FestivalItem, b: FestivalItem) => {
          return isSameData(a, b);
        }) &&
        other.type === this.type;
  }
}

export class FestivalDivision implements ICloneable, IDataComparable {
  constructor(
        public readonly index: number,
        public readonly divisionId: string,
        public readonly categories: Map<string, FestivalCategory>,
  ) { }

  static decode(map: SerializedData, divisionId: string): FestivalDivision {
    return new FestivalDivision(
        MapUtils.get<number>(map, 'index'),
        divisionId,
        MapUtils.getMap<string, FestivalCategory>(map, 'categories', (k: string, v: unknown) => {
          return {
            key: k,
            value: FestivalCategory.decode(v as SerializedData),
          };
        }),
    );
  }

  encode(): SerializedData {
    return {
      'index': this.index,
      'categories': mapToData(this.categories, (k: string, v: FestivalCategory) => {
        return {
          key: k,
          value: v.encode(),
        };
      }),
    };
  }

  clone(): this {
    return new FestivalDivision(
        this.index,
        this.divisionId,
        cloneMap(this.categories, (v: FestivalCategory) => {
          return v.clone();
        }),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.index === this.index &&
        other.divisionId === this.divisionId &&
        isSameMap(other.categories, this.categories, (lh: FestivalCategory, rh: FestivalCategory) => {
          return isSameData(lh, rh);
        });
  }
}

export class FestivalDistribution extends DataModel {
  static readonly TYPE_ID: string = 'cbb2b9fa0d3e4ce3b5a1e4d802812582';

  constructor(
    public readonly festivalId: string,
    public readonly pointsOfInterest: Map<string, FestivalDivision>,
  ) {
    super();
  }

  get $typeId(): string {
    return FestivalDistribution.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): FestivalDistribution {
    return new FestivalDistribution(
        MapUtils.get<string>(map, 'festivalId'),
        MapUtils.getMap<string, FestivalDivision>(map, 'pointsOfInterest', (k: string, v: unknown) => {
          return {
            key: k,
            value: FestivalDivision.decode(map, k),
          };
        }),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'festivalId': this.festivalId,
      'pointsOfInterest': mapToData(this.pointsOfInterest, (k: string, v: FestivalDivision) => {
        return {
          key: k,
          value: v.encode(),
        };
      }),
    };
  }

  clone(): this {
    return new FestivalDistribution(
        this.festivalId,
        cloneMap(this.pointsOfInterest, (v: FestivalDivision) => {
          return v.clone();
        }),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.festivalId === this.festivalId &&
        isSameMap(other.pointsOfInterest, this.pointsOfInterest, (lh: FestivalDivision, rh: FestivalDivision) => {
          return isSameData(lh, rh);
        });
  }
}
